import { riskLevelToLabel } from '../../../../utils/constants';
import { getJiraLinkValidation, updateMoment } from '../../RsdForms/api';

/**
 * Field mapping for phased layouts - defines which fields belong to which section within a phase
 */
export const PHASED_FIELD_MAPPING = {
  generalInfo: [
    'momentType',
    'riskLevel',
    'tier',
    'revenueImpact',
    'event',
    'parentMoment'
  ] as string[],
  dates: [
    'targetQuarter',
    'releaseQuarter',
    'targetMonth',
    'releaseMonth',
    'targetStart',
    'releaseDate',
    'targetDateStatus',
    'releaseDateStatus',
    'effectiveDateStatus'
  ] as string[],
  primaryContacts: [
    'productMarketingManagers',
    'programManagers',
    'productManagers'
  ] as string[],
  phaseLevel: [
    'status',
    'inheritMilestones',
    'milestoneId',
    'showOnRoadmap',
    'secondaryDateStatus'
  ] as string[]
};

/**
 * Creates the correct API payload for updating a field in a specific phase
 * @param phaseId - The unique ID of the phase to update (e.g., "e98334f0-ddb1-4b83-a05f-36cbdce3e67a")
 * @param fieldName - The field to update (e.g., "tier", "riskLevel")
 * @param value - The new value for the field
 * @param currentPhaseData - Current phase data for merging (optional)
 * @param phaseName - The name of the phase (required for root-level fields)
 * @returns The correctly nested payload for the API
 */
export function createPhasedFieldUpdatePayload(
  phaseId: string,
  fieldName: string,
  value: any,
  currentPhaseData?: any,
  phaseName?: string
): Record<string, any> {
  const payloadValue =
    fieldName === 'riskLevel' ? riskLevelToLabel(value) : value;

  // Find which categories the field belongs to
  for (const [categories, fields] of Object.entries(PHASED_FIELD_MAPPING)) {
    if (fields.includes(fieldName)) {
      switch (categories) {
        case 'generalInfo':
          return {
            releasePhases: [
              {
                id: phaseId,
                ...(phaseName && { phaseName }),
                generalInfo: {
                  [fieldName]: payloadValue
                }
              }
            ]
          };

        case 'dates':
          return {
            releasePhases: [
              {
                id: phaseId,
                ...(phaseName && { phaseName }),
                generalInfo: {
                  dates: {
                    ...(currentPhaseData?.generalInfo?.dates || {}),
                    [fieldName]: payloadValue
                  }
                }
              }
            ]
          };

        case 'primaryContacts':
          return {
            releasePhases: [
              {
                id: phaseId,
                primaryContacts: {
                  [fieldName]: payloadValue
                }
              }
            ]
          };

        case 'phaseLevel':
          return {
            releasePhases: [
              {
                id: phaseId,
                ...(phaseName && { phaseName }),
                [fieldName]: payloadValue
              }
            ]
          };

        default:
          return {
            releasePhases: [
              {
                id: phaseId,
                [fieldName]: payloadValue
              }
            ]
          };
      }
    }
  }

  // If field is not found in mapping, place it at phase level
  return {
    releasePhases: [
      {
        id: phaseId,
        [fieldName]: payloadValue
      }
    ]
  };
}

/**
 * Generic field update function for phased layout
 * @param issueId - The issue ID to update
 * @param phaseId - The phase ID to update
 * @param fieldName - The field to update
 * @param value - The new value
 * @param onSuccess - Success callback
 * @param onError - Error callback
 * @param currentPhaseData - Current phase data for merging (optional)
 * @param phaseName - The phase name (required for root-level fields)
 */
export async function updatePhasedField(
  issueId: string,
  phaseId: string,
  fieldName: string,
  value: any,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void,
  currentPhaseData?: any,
  phaseName?: string
): Promise<void> {
  if (!issueId) {
    onError?.(new Error('No issue ID provided'));
    return;
  }

  if (!phaseId) {
    onError?.(new Error('No phase ID provided'));
    return;
  }

  try {
    const payload = createPhasedFieldUpdatePayload(
      phaseId,
      fieldName,
      value,
      currentPhaseData,
      phaseName
    );
    const response = await updateMoment(issueId, payload);
    onSuccess?.(response);
  } catch (error) {
    onError?.(error);
  }
}

/**
 * Specialized update function for tier field in phased layout
 * @param issueId - The issue ID to update
 * @param phaseId - The phase ID to update
 * @param tierValue - The new tier value (L1, L2, L3, Other)
 * @param onSuccess - Success callback
 * @param onError - Error callback
 * @param phaseName - The phase name (required for root-level fields)
 */
export async function updatePhasedTierField(
  issueId: string,
  phaseId: string,
  tierValue: string,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void,
  phaseName?: string
): Promise<void> {
  await updatePhasedField(
    issueId,
    phaseId,
    'tier',
    tierValue,
    onSuccess,
    onError,
    undefined, // currentPhaseData
    phaseName
  );
}

/**
 * Specialized update function for risk level field in phased layout
 * @param issueId - The issue ID to update
 * @param phaseId - The phase ID to update
 * @param riskLevel - UI risk level (Green, Yellow, Red) — sent to API as Low, Moderate, High
 * @param onSuccess - Success callback
 * @param onError - Error callback
 * @param phaseName - The phase name (required for root-level fields)
 */
export async function updatePhasedRiskLevelField(
  issueId: string,
  phaseId: string,
  riskLevel: string,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void,
  phaseName?: string
): Promise<void> {
  await updatePhasedField(
    issueId,
    phaseId,
    'riskLevel',
    riskLevel,
    onSuccess,
    onError,
    undefined, // currentPhaseData
    phaseName
  );
}

/**
 * Specialized update function for revenue impact field in phased layout
 * @param issueId - The issue ID to update
 * @param phaseId - The phase ID to update
 * @param revenueImpactValue - The new revenue impact value (Yes, No) - will be converted to boolean
 * @param onSuccess - Success callback
 * @param onError - Error callback
 * @param phaseName - The phase name (required for root-level fields)
 */
export async function updatePhasedRevenueImpactField(
  issueId: string,
  phaseId: string,
  revenueImpactValue: string,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void,
  phaseName?: string
): Promise<void> {
  // Convert "Yes"/"No" string to boolean (true/false) to match genai format
  const booleanValue = revenueImpactValue === 'Yes';

  await updatePhasedField(
    issueId,
    phaseId,
    'revenueImpact',
    booleanValue, // Send boolean instead of string
    onSuccess,
    onError,
    undefined, // currentPhaseData
    phaseName
  );
}

/**
 * Specialized update function for date fields in phased layout
 * @param issueId - The issue ID to update
 * @param phaseId - The phase ID to update
 * @param dateField - The date field name (targetQuarter, releaseDate, etc.)
 * @param dateValue - The new date value
 * @param onSuccess - Success callback
 * @param onError - Error callback
 * @param currentPhaseData - Current phase data for merging dates
 */
export async function updatePhasedDateField(
  issueId: string,
  phaseId: string,
  dateField: string,
  dateValue: any,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void,
  currentPhaseData?: any
): Promise<void> {
  await updatePhasedField(
    issueId,
    phaseId,
    dateField,
    dateValue,
    onSuccess,
    onError,
    currentPhaseData
  );
}

/**
 * Specialized update function for status field in phased layout
 * @param issueId - The issue ID to update
 * @param phaseId - The phase ID to update
 * @param status - The new status (ACTIVE, COMPLETED, N/A)
 * @param onSuccess - Success callback
 * @param onError - Error callback
 * @param phaseName - The phase name (required for root-level fields)
 */
export async function updatePhasedStatusField(
  issueId: string,
  phaseId: string,
  status: string,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void,
  phaseName?: string,
  completionDate?: string
): Promise<void> {
  // Create a custom payload for status updates that includes completion date
  const payload = {
    releasePhases: [
      {
        id: phaseId,
        ...(phaseName && { phaseName }),
        status: status,
        ...(status === 'COMPLETED' &&
          completionDate && { statusUpdatedAt: completionDate })
      }
    ]
  };

  try {
    const response = await updateMoment(issueId, payload);
    onSuccess?.(response);
  } catch (error) {
    onError?.(error);
  }
}

/**
 * Specialized update function for primary contacts in phased layout
 * @param issueId - The issue ID to update
 * @param phaseId - The phase ID to update
 * @param primaryContacts - The primary contacts object with productMarketingManagers, productManagers, programManagers
 * @param onSuccess - Success callback
 * @param onError - Error callback
 */
export async function updatePhasedPrimaryContacts(
  issueId: string,
  phaseId: string,
  primaryContacts: any,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  const payload = {
    releasePhases: [
      {
        id: phaseId,
        primaryContacts: primaryContacts
      }
    ]
  };

  try {
    const response = await updateMoment(issueId, payload);
    if (onSuccess) {
      onSuccess(response);
    }
  } catch (error) {
    if (onError) {
      onError(error);
    }
  }
}

/**
 * Enhanced primary contacts update function that supports individual contact IDs and delete operations
 * @param issueId - The issue ID to update
 * @param phaseId - The phase ID to update
 * @param contactUpdates - Object containing contact updates with IDs and delete flags
 * @param onSuccess - Success callback
 * @param onError - Error callback
 */
export async function updatePhasedPrimaryContactsWithIds(
  issueId: string,
  phaseId: string,
  contactUpdates: {
    productMarketingManagers?: Array<{
      id?: string;
      uid?: string;
      mail?: string;
      name?: string;
      status?: string;
      _delete?: boolean;
    }>;
    programManagers?: Array<{
      id?: string;
      uid?: string;
      mail?: string;
      name?: string;
      status?: string;
      _delete?: boolean;
    }>;
    productManagers?: Array<{
      id?: string;
      uid?: string;
      mail?: string;
      name?: string;
      status?: string;
      _delete?: boolean;
    }>;
  },
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  const payload = {
    releasePhases: [
      {
        id: phaseId,
        primaryContacts: contactUpdates
      }
    ]
  };

  try {
    const response = await updateMoment(issueId, payload);
    if (onSuccess) {
      onSuccess(response);
    }
  } catch (error) {
    if (onError) {
      onError(error);
    }
  }
}

export const validateJiraLink = async (
  issueId: string,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> => {
  try {
    const response = await getJiraLinkValidation(issueId);
    if (onSuccess) {
      onSuccess(response);
    }
  } catch (error) {
    if (onError) {
      onError(error);
    }
  }
};

/**
 * Update decisions with their own IDs and delete support
 * @param issueId - The issue ID to update
 * @param decisions - Array of decision objects with id and _delete flag
 * @param onSuccess - Success callback
 * @param onError - Error callback
 */
export async function updateDecisionsWithIds(
  issueId: string,
  decisions: Array<{
    id?: string;
    date?: string;
    description?: string;
    _delete?: boolean;
  }>,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  const payload = {
    decisions: decisions
  };

  try {
    const response = await updateMoment(issueId, payload);
    if (onSuccess) {
      onSuccess(response);
    }
  } catch (error) {
    if (onError) {
      onError(error);
    }
  }
}

/**
 * Update DACI contacts with their own IDs and delete support
 * @param issueId - The issue ID to update
 * @param daciContacts - DACI contacts object with flat contacts array
 * @param onSuccess - Success callback
 * @param onError - Error callback
 */
export async function updateDACIContactsWithIds(
  issueId: string,
  daciContacts: {
    contacts: Array<{
      id?: string;
      daciRole: string; // Driver, Approver, Contributor, Informed
      role?: string; // Product Manager, Program Manager, etc.
      name: string;
      uid?: string;
      imageUrl: string;
      _delete?: boolean;
    }>;
  },
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  const payload = {
    daciContacts: daciContacts
  };

  try {
    const response = await updateMoment(issueId, payload);
    if (onSuccess) {
      onSuccess(response);
    }
  } catch (error) {
    if (onError) {
      onError(error);
    }
  }
}

/**
 * Update additional info with their own IDs and delete support
 * @param issueId - The issue ID to update
 * @param additionalInfo - Array of additional info objects with id and _delete flag
 * @param onSuccess - Success callback
 * @param onError - Error callback
 */
export async function updateAdditionalInfoWithIds(
  issueId: string,
  additionalInfo: Array<{
    id?: string;
    title?: string;
    url?: string;
    _delete?: boolean;
  }>,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  const payload = {
    additionalInfo: additionalInfo
  };

  try {
    const response = await updateMoment(issueId, payload);
    if (onSuccess) {
      onSuccess(response);
    }
  } catch (error) {
    if (onError) {
      onError(error);
    }
  }
}

/**
 * Update event field for a specific phase
 * @param issueId - The issue ID to update
 * @param phaseId - The phase ID to update
 * @param eventData - The event data { id: string, name: string }
 * @param phaseName - The name of the phase
 * @param status - The phase status (e.g., 'ACTIVE')
 * @param onSuccess - Optional success callback
 * @param onError - Optional error callback
 */
export async function updatePhasedEventField(
  issueId: string,
  phaseId: string,
  eventData: { id: string | null; name: string },
  phaseName?: string,
  status?: string,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  try {
    const payload = {
      releasePhases: [
        {
          id: phaseId,
          ...(phaseName && { phaseName }),
          ...(status && { status }),
          generalInfo: {
            event: eventData
          }
        }
      ]
    };

    const response = await updateMoment(issueId, payload);

    if (onSuccess) {
      onSuccess(response);
    }
  } catch (error) {
    if (onError) {
      onError(error);
    }
  }
}

/**
 * Update parent moment field for a specific phase
 * @param issueId - The issue ID to update
 * @param phaseId - The phase ID to update
 * @param parentMomentData - The parent moment data { id: string, name: string }
 * @param phaseName - The name of the phase
 * @param status - The phase status (e.g., 'ACTIVE')
 * @param onSuccess - Optional success callback
 * @param onError - Optional error callback
 */
export async function updatePhasedParentMomentField(
  issueId: string,
  phaseId: string,
  parentMomentData: { id: string | null; name: string },
  phaseName?: string,
  status?: string,
  onSuccess?: (response: any) => void,
  onError?: (error: any) => void
): Promise<void> {
  try {
    const payload = {
      releasePhases: [
        {
          id: phaseId,
          ...(phaseName && { phaseName }),
          ...(status && { status }),
          generalInfo: {
            parentMoment: parentMomentData
          }
        }
      ]
    };

    const response = await updateMoment(issueId, payload);

    if (onSuccess) {
      onSuccess(response);
    }
  } catch (error) {
    if (onError) {
      onError(error);
    }
  }
}

/**
 * Helper function to get field category for phased layout
 * @param fieldName - The field name to categorize
 * @returns The categories the field belongs to
 */
export function getPhasedFieldCategory(fieldName: string): string {
  for (const [categories, fields] of Object.entries(PHASED_FIELD_MAPPING)) {
    if (fields.includes(fieldName)) {
      return categories;
    }
  }
  return 'phaseLevel';
}

export default {
  createPhasedFieldUpdatePayload,
  updatePhasedField,
  updatePhasedTierField,
  updatePhasedRiskLevelField,
  updatePhasedRevenueImpactField,
  updatePhasedEventField,
  updatePhasedParentMomentField,
  updatePhasedDateField,
  updatePhasedStatusField,
  updatePhasedPrimaryContacts,
  getPhasedFieldCategory,
  PHASED_FIELD_MAPPING
};
